<template>
  <component
    :is="type === 'radio' || type === 'checkbox' ? 'div' : 'label'"
    :class="[
      type === 'radio' ? 'radio' : type === 'checkbox' ? 'checkbox' : '',
      state,
      price ? 'price' : '',
      inline ? 'inline' : 'not-inline',
      `form-field-${type}`,
      {
        required: $attrs.required,
      },
    ]"
    class="form-field form-label form-group"
  >
    <span v-if="hint && hint.text" class="form-label-text has-hint">
      <span class="form-label-text-item" v-text="label" />
      <component
        :is="hint.url || hint.scrollTo ? AtomsButton : 'span'"
        :to="hint.url"
        :scroll-to="hint.scrollTo"
        class="form-label-hint"
        :plain="!!(hint.url || hint.scrollTo)"
        v-text="hint.text"
      />
    </span>
    <span v-else class="form-label-text" v-html="label" />
    <component
      :is="component"
      v-if="component"
      v-bind="{
        index,
        type,
        title: hint.text,
        attrs: $attrs,
        options,
        suggestedAmounts,
      }"
      v-model="value"
    />
    <textarea
      v-else-if="type === 'textarea'"
      v-model="value"
      v-bind="{
        ...$attrs,
        condition: undefined,
        style: undefined,
      }"
      :title="hint.text"
      type="textarea"
      class="form-input"
    />
    <input
      v-else
      v-model="value"
      v-bind="{
        ...$attrs,
        condition: undefined,
        style: undefined,
      }"
      :title="hint.text"
      :type="type"
      class="form-input"
    />
  </component>
</template>

<script setup lang="ts">
import type { FormFieldOption } from '~~/types'
const AtomsButton = resolveComponent('LazyAtomsButton')

const FormFieldGiftaid = resolveComponent('LazyFormFieldGiftaid')
const FormFieldOptions = resolveComponent('LazyFormFieldOptions')
const FormFieldAmount = resolveComponent('LazyFormFieldAmount')
const FormFieldCheck = resolveComponent('LazyFormFieldCheck')

const props = defineProps({
  index: { type: Number, default: 0 },
  label: { type: String, default: '' },
  type: { type: String, default: '' },
  hint: { type: Object, default: () => ({ text: '' }) },
  price: { type: Number, default: undefined },
  inline: { type: Boolean, default: false },
  options: { type: Array as () => FormFieldOption[], default: undefined },
  modelValue: { type: [String, Number, Boolean, Array] },
  suggestedAmounts: { type: Array, default: undefined },
})
const emit = defineEmits(['update:modelValue'])

const value = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
})

const component = computed(() =>
  props.options
    ? FormFieldOptions
    : props.type === 'checkbox' || props.type === 'radio'
    ? FormFieldCheck
    : props.type === 'amount'
    ? FormFieldAmount
    : props.type === 'giftaid'
    ? FormFieldGiftaid
    : undefined
)

const state = computed(
  () =>
    typeof value.value === 'string' &&
    value.value?.length &&
    (props.type === 'email'
      ? validEmail(value.value)
        ? 'valid'
        : 'invalid'
      : props.type === 'tel'
      ? validPhone(value.value)
        ? 'valid'
        : 'invalid'
      : undefined)
)
</script>

<style lang="scss">
:root {
  --field-cl: var(--black);
  --field-inv: var(--white);
  --field-bg: rgba(0, 0, 0, 0.07);
  --field-bd: none;
  --field-accent: var(--body);
}
.form {
  &-group {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: czn.$xl + czn.$md;

    &:not(.radio):not(.checkbox) {
      @include media('<=tablet') {
        flex-flow: column;

        .form {
          &-label-text {
            margin-bottom: 1rem;
          }
          &-input {
            align-self: stretch;
          }
        }
        .lecture {
          width: 100%;
        }
      }
    }
    &.checkbox {
      > * {
        flex: unset;
      }

      > .form-label-text {
        min-width: 20%;
      }
    }

    &.price {
      justify-content: flex-start;
      .form-input {
        max-width: 5rem;
        padding-right: 0.25rem;
      }
      .form-label-price {
        padding-left: 1rem;
      }
      .form-label-sum {
        margin-left: auto;
      }
    }
  }
  &-inline-group {
    display: flex;
    flex-flow: row wrap;
    width: 100%;

    margin-bottom: -1rem;

    .form-toggle {
      order: -1;
    }
    .form-label {
      margin-right: 2em;
      margin-bottom: 1rem;

      cursor: pointer;
    }
  }
  &-label {
    display: flex;
    align-items: center;
    width: 100%;

    &.not-inline {
      flex-flow: column;

      .form-inline-group {
        flex-flow: column;
        align-items: flex-start;
      }

      .form-label-text {
        position: relative;
        margin-right: 0;
        margin-bottom: 1rem;
        width: 100%;
      }
    }

    &-text {
      flex: 0 1 25%;
      margin-right: 1em;
      position: relative;
    }

    &-hint {
      display: flex !important;
      margin-top: 0.25rem;

      font-size: 0.9rem;
      font-style: italic;

      opacity: 0.8;

      &[type='button'] {
        color: var(--field-accent);
      }
    }

    &.invalid .form-input {
      border-color: darkred !important;
    }
    &.valid .form-input {
      border-color: green !important;
    }

    &.required,
    .required {
      .form-label-text:not(.has-hint)::after,
      .form-label-text-item::after {
        margin-left: 0.5rem;
        vertical-align: super;
        font-size: 0.7em;
        color: var(--secondary);

        content: '★';
      }

      :not(:checked) ~ .form-checkbox {
        border-color: darkred !important;
        color: white;
        .form-checkbox-active {
          background: darkred;
          color: white;
        }
      }
      :checked ~ .form-checkbox {
        .form-checkbox-active {
          background: var(--green);
          color: darkgreen;
          border-color: darkgreen;
        }
        .active {
          color: darkgreen;
        }
      }
    }

    &:hover,
    &:focus,
    &:focus-within {
      // color: var(--field-cl);

      .form-input {
        background: var(--field-bg);
        border-color: var(--field-accent);

        &::placeholder {
          color: rgba(var(--field-accent), 0.4);
        }
      }

      .form-toggle::before {
        opacity: 0.4;
      }
    }
  }
  &-input {
    display: block;
    flex: 1 1;
    width: 100%;
    max-width: 100%;
    padding: 1rem;
    border-radius: var(--br-items-sm);

    &[type='textarea'] {
      min-width: 100%;
      flex-basis: unset;
    }

    color: var(--field-cl);

    background: var(--field-bg);
    border: 2px solid transparent;

    &::placeholder {
      color: rgba(var(--field-cl), 0.6);
    }

    &:checked {
      ~ .form-toggle::before {
        opacity: 1;
      }
      ~ .form-yes {
        &::before {
          transform: translateX(-2rem);
          opacity: 0;
        }
        &::after {
          transform: none;
          opacity: 1;
        }
      }
      ~ .form-checkbox .form-checkbox-active {
        transform: translateX(100%);
      }
    }

    &[type='checkbox'],
    &[type='radio'] {
      flex: 0 0 0;
      width: 0;
      height: 0;
      margin: 0;

      opacity: 0;
    }
  }
  &-radio-text {
    display: flex;
    margin-right: 2rem;
  }

  &-toggle {
    $size: 1rem;
    position: relative;

    display: block;
    width: $size;
    min-width: $size;
    height: $size;
    margin-right: 1rem;

    background: var(--field-bg);
    border: 2px solid var(--field-accent);
    cursor: pointer;

    &::before {
      inset: 2px;
      display: block;

      background: var(--field-accent);
      opacity: 0;

      content: '';
    }
  }

  &-yes {
    position: relative;

    min-height: 1rem;
    &::before,
    &::after {
      position: absolute;
      left: 0;

      transition: transform var(--ts);
    }
    &::before {
      content: 'No';
    }
    &::after {
      transform: translateX(2rem);
      opacity: 0;

      content: 'Yes';
    }
  }
}
</style>
